import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useState } from 'react';

export const ConversationsContext = createContext({});

export const useConversationsContext = () => {
  const context = useContext(ConversationsContext);
  if (!context) {
    throw new Error('useConversations should be used inside ConversationsProvider');
  }
  return context;
};

export const ConversationsProvider = ({ children, selectedConversationId, navigationKey }) => {
  const [conversationsData, setConversationsData] = useState({
    selectedConversationId: selectedConversationId || null,
    conversationInfo: null,
  });

  // The provider stays mounted while the user navigates within the messaging page, so the
  // initial useState value alone would ignore a new ?id= — which is exactly what happens
  // when the nav bell is used from the messaging page itself. Follow the prop instead.
  //
  // The effect re-runs on every navigation, not only when the id in the URL changes:
  // clicking a conversation in the list moves the context selection without touching the
  // URL, so the URL and the context selection can hold different ids. Reopening the URL's
  // conversation from the bell then pushes the same id again, and keying on the id alone
  // would treat that as "nothing happened" and leave the stale manual selection in place.
  // navigationKey changes on every push/replace, which makes a repeat of the same id a
  // distinct navigation. The comparison runs against the live context selection inside the
  // updater, so it can't read a stale value through the effect's closure.
  // A navigation without an id leaves the current selection untouched, matching the
  // behaviour before this effect existed. Note that the bell's "view all unread" link
  // navigates that way, so the previously opened conversation stays selected and the
  // list's auto-select-first effect skips it — pre-existing, tracked separately.
  useEffect(() => {
    const nextId = selectedConversationId || null;
    if (!nextId) return;

    // The URL always yields a string while a list click stores the server's numeric id,
    // so the two are compared as strings to keep a same-conversation re-push a no-op.
    setConversationsData(prevData => (
      String(prevData.selectedConversationId) === String(nextId)
        ? prevData
        : { ...prevData, selectedConversationId: nextId }
    ));
  }, [selectedConversationId, navigationKey]);

  const updateConversationsData = (newData) => {
    setConversationsData(prevData => ({
      ...prevData,
      ...newData
    }));
  }

  const value = {
    conversationsData,
    updateConversationsData,
  }

  return (
    <ConversationsContext.Provider value={value}>
      {children}
    </ConversationsContext.Provider>
  );
}

ConversationsProvider.propTypes = {
  children: PropTypes.node,
  selectedConversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  navigationKey: PropTypes.string,
};
